/* eslint-disable i18next/no-literal-string */
import {useState} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {usePageTitle} from '@/hooks/use-page-title'
import {useTypedEntityAttributes} from '@/hooks/use-typed-entity-attributes'
import {pickTypeValueLabel} from '@/lib/promoted-types-label'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardContent} from '@/components/ui/card'
import {InlineRow} from '@/components/shared/InlineRow'
import {FormGrid} from '@/components/shared/FormGrid'
import {PageBackButton} from '@/components/shared/PageBackButton'
import {SectionContainer} from '@/components/shared/SectionContainer'
import {AttributeFields} from '@/components/attributes/AttributeFields'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {AttributeValueType} from '@/types/attribute'
import type {AttributeDefinition, SetAttributeRequest} from '@/types/attribute-definition'
import {PartyCreateRequestFields, PartyResponseFields} from '@/types/party'
import type {PartyCreateRequest} from '@/types/party'
import {useFormSubmit} from '@/hooks/use-form-submit'

function buildSetAttributeRequest(def: AttributeDefinition, raw: string): SetAttributeRequest {
    const value = raw.trim()
    switch (def.valueType) {
        case AttributeValueType.NUMBER:
            return {attributeName: def.name, valueType: def.valueType, valueNumber: Number(value)}
        case AttributeValueType.BOOLEAN:
            // eslint-disable-next-line i18next/no-literal-string -- boolean string-form sentinel from <input>
            return {attributeName: def.name, valueType: def.valueType, valueBoolean: value === 'true'}
        case AttributeValueType.DATE:
            return {attributeName: def.name, valueType: def.valueType, valueDate: value}
        case AttributeValueType.TIMESTAMP:
            return {attributeName: def.name, valueType: def.valueType, valueTimestamp: value}
        case AttributeValueType.STRING:
        case AttributeValueType.JSON:
        case AttributeValueType.REFERENCE:
            return {attributeName: def.name, valueType: def.valueType, valueString: value}
    }
}

/**
 * Create Party page invoked from /identities/parties/by-type/{TYPE}/new.
 * The partyType is locked from the URL; the form collects dynamic attributes.
 */
export function CreateParty() {
    const {typeCode} = useParams<{typeCode: string}>()
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {partyTypes} = useAppConfig()
    const {i18n: i18nInstance} = useTranslation()
    const lang = i18nInstance.language

    const partyTypeCode = (typeCode ?? '').toUpperCase()
    const singularLabel = pickTypeValueLabel(partyTypes, partyTypeCode, lang) ?? labelOf(partyTypes, partyTypeCode)
    const pageTitle = `${i18n.common.create} ${singularLabel}`

    usePageTitle(pageTitle)

    const [attributeValues, setAttributeValues] = useState<Record<string, string>>({})

    const {formDefinitions, templateAttributeNames, isLoading} = useTypedEntityAttributes({
        entityType: AttributeEntityType.PARTY,
        typeCode: partyTypeCode,
    })

    const renderDefinitions = formDefinitions.filter(d => d.name !== PartyCreateRequestFields.partyType)

    const createMutation = useEntityMutation({
        mutationFn: (request: PartyCreateRequest) => services.parties.create(request),
        successMessage: i18n.parties.createSuccess,
        onSuccess: (party) => {
            navigate(routes.identityManagement.parties.detail(party.id))
        },
    })

    const {submit, fieldErrors} = useFormSubmit({
        fields: renderDefinitions.map((def) => {
            const label = def.displayName || def.name
            const value = attributeValues[def.id] ?? ''
            const isDisplayName = def.name === PartyResponseFields.derivedDisplayName
            const focusId = isDisplayName
                ? 'input-resource-display-name'
                : `attr-${def.name}`
            const isStaticRequired = isDisplayName
            return {
                id: def.id,
                label,
                value,
                required: isStaticRequired || def.required || templateAttributeNames.includes(def.name),
                focusId,
            }
        }),
        onSubmit: () => {
            const attributes: SetAttributeRequest[] = renderDefinitions
                .map((def) => {
                    const raw = attributeValues[def.id] ?? ''
                    if (!raw.trim()) return null
                    return buildSetAttributeRequest(def, raw)
                })
                .filter((r): r is SetAttributeRequest => r != null)
            createMutation.mutate({partyType: partyTypeCode, attributes})
        },
    })

    return (
        <SectionContainer>
            <PageBackButton to={routes.identityManagement.parties.byType(partyTypeCode)}/>

            <Card className="max-w-2xl">
                <CardHeader>
                    <CardTitle data-testid="card-title-create-party">{pageTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={submit}>
                        <FormGrid>
                            <AttributeFields
                                definitions={renderDefinitions}
                                values={attributeValues}
                                onChange={(defId, v) =>
                                    setAttributeValues((s) => ({...s, [defId]: v}))}
                                templateAttributeNames={templateAttributeNames}
                                errors={fieldErrors}
                            />
                        </FormGrid>

                        <InlineRow className="justify-end pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                data-testid="btn-cancel-create-party"
                                onClick={() => navigate(routes.identityManagement.parties.byType(partyTypeCode))}
                            >
                                {i18n.common.cancel}
                            </Button>
                            <Button
                                type="submit"
                                data-testid="btn-submit-create-party"
                                disabled={isLoading || createMutation.isPending}
                            >
                                {pageTitle}
                            </Button>
                        </InlineRow>
                    </form>
                </CardContent>
            </Card>
        </SectionContainer>
    )
}
